import { Fragment, useState } from 'react';
import { JxtInput } from '@jooxter/ui';
import { IJxtAttendeeCombobox, JxtAttendeeOption, JxtAttendeeOptionTypeEnum } from './types';
import { useFetchExternalAttendeesSearch, useFetchUser, useFetchUsersSearch } from '../../queries';
import { useTranslation } from 'react-i18next';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import JxtDropdownItem from '@jooxter/ui/src/components/JxtDropdownItem';
import { Controller, useForm } from 'react-hook-form';
import { TextHighlight } from '@jooxter/utils';
import { useDebouncedValue } from '@mantine/hooks';

const EMAIL_REGEX = /\S+@\S+\.\S+/;
const ATTENDEES_FIELD_NAME = 'attendees';

export const JxtAttendeeCombobox = ({ onAttendeeSelect, placeholder, hasErrors = false }: IJxtAttendeeCombobox) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const { user } = useFetchUser();
  const isSearchEnabled = debouncedQuery.length > 0;

  const { users = [], isLoading: isLoadingUsers } = useFetchUsersSearch(
    { globalSearch: debouncedQuery, size: 25 },
    isSearchEnabled
  );
  const { externalAttendees, isLoading: isLoadingExternalAttendees } = useFetchExternalAttendeesSearch(
    { globalSearch: debouncedQuery, size: 25 },
    isSearchEnabled
  );
  const isLoading = isLoadingUsers || isLoadingExternalAttendees;
  const isLoaded = !!(users?.length || externalAttendees?.length);

  const { register, formState, getFieldState, setValue, clearErrors, control, trigger } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const { invalid, error, isTouched } = getFieldState(ATTENDEES_FIELD_NAME, formState);
  const errorMessage = invalid && error?.message;

  const handleAttendeeSelection = (option: JxtAttendeeOption | null) => {
    if (!option) {
      return;
    }
    if (option.type === JxtAttendeeOptionTypeEnum.NEW_EXTERNAL && (isLoading || !EMAIL_REGEX.test(option.text))) {
      trigger(ATTENDEES_FIELD_NAME);
      return;
    }
    onAttendeeSelect?.(option);
    setQuery('');
    setValue(ATTENDEES_FIELD_NAME, '');
    clearErrors(ATTENDEES_FIELD_NAME);
  };

  return (
    <Fragment>
      <div className="relative col-start-2 row-start-1">
        <Combobox onChange={handleAttendeeSelection}>
          <Controller
            name={ATTENDEES_FIELD_NAME}
            control={control}
            render={({ field }) => (
              <ComboboxInput
                {...field}
                as={JxtInput}
                register={register}
                formState={formState}
                getFieldState={getFieldState}
                placeholder={placeholder}
                validationSchema={{
                  validate: {
                    checkEmail: (value?: string) =>
                      !value || EMAIL_REGEX.test(value) || t<string>('login-email-required'),
                  },
                }}
                onChange={(event) => {
                  setQuery(event.target.value);
                  clearErrors(ATTENDEES_FIELD_NAME);
                }}
                showErrorMessages={false}
                errorStyle={isTouched && (hasErrors || !!errorMessage)}
              />
            )}
          />

          <ComboboxOptions
            modal={false}
            className="absolute z-10 max-h-[300px] w-full overflow-auto rounded-lg bg-white my-2 py-1 border border-neutral-30 shadow-elevation-2"
          >
            {!isLoaded && (
              <ComboboxOption
                as={JxtDropdownItem}
                value={{ type: JxtAttendeeOptionTypeEnum.NEW_EXTERNAL, text: query }}
              >
                <p className="truncate">{query}</p>
              </ComboboxOption>
            )}
            {users.map((u) => (
              <ComboboxOption
                as={JxtDropdownItem}
                key={`user-${u.id}`}
                value={{
                  type: JxtAttendeeOptionTypeEnum.INTERNAL,
                  id: u.id,
                  text: `${u.firstname} ${u.lastname}` + (u.id === user?.id ? ` (${t('you')})` : ''),
                }}
              >
                <p className="truncate">
                  <TextHighlight
                    text={`${u.firstname} ${u.lastname} (${u.email})` + (u.id === user?.id ? ` (${t('you')})` : '')}
                    searchTerm={query}
                  />
                </p>
              </ComboboxOption>
            ))}
            {externalAttendees.map((e) => (
              <ComboboxOption
                as={JxtDropdownItem}
                key={`external-${e.id}`}
                value={{ type: JxtAttendeeOptionTypeEnum.EXTERNAL, id: e.id, text: e.email }}
              >
                <p className="truncate">
                  <TextHighlight text={e.email} searchTerm={query} />
                </p>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </Combobox>
      </div>
      {invalid && errorMessage ? (
        <p className="text-red-100 pt-1 col-start-2 row-start-2">{errorMessage}</p>
      ) : (
        <p className="text-body-xs text-neutral-80 col-start-2 row-start-2 pt-1">
          {t('booking.attendees.search.helper')}
        </p>
      )}
    </Fragment>
  );
};
